import { create } from 'zustand';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'owner' | 'admin' | 'manager' | 'employee' | 'inventory_manager';
  tenantId?: string | null;
  locationId?: string | null;
  locationIds?: string[];
  twoFactorEnabled?: boolean;
  /** Permissions granted on top of the role (see @supernova/shared permissions). */
  extraPermissions?: string[];
  /** The user's full permission set, worked out by auth. Null for super admins. */
  permissions?: string[] | null;
  /** The tenant's custom role this user holds. `role` is then its base level. */
  customRole?: { id: string; name: string } | null;
}

export interface AuthTenant {
  id: string;
  externalId?: string | null;
  name: string;
  slug: string;
  isActive: boolean;
  // Super-admin gate for "Add Company" on the location picker. Optional in
  // the type because legacy payloads may omit it; the UI treats anything other
  // than the explicit value `true` as disabled so the gate is closed by
  // default — matching the DB default and the super-admin-must-enable model.
  canAddLocations?: boolean;
  onboardingCompleted: boolean;
  // The KIND of account, not a feature flag. 'esl' is an external-POS or
  // standalone shelf-label client: they register in this portal like anyone
  // else, but they are not a payments merchant — no KYC, no bank, and the only
  // module they are ever offered is Shelf Labels.
  type?: 'company' | 'warehouse' | 'esl';
  phone?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  defaultTaxRate?: string | null;
  // Super-admin per-tenant gate. When true, the dashboard shows the generic
  // (common) product image matched by keyword on the product details page,
  // mirroring what POS devices display. Optional/undefined treated as off.
  useGenericProductImages?: boolean;
  // Super-admin per-tenant gate. When true, the business may upload its own
  // product image on the product details page (uploaded to the products
  // service → S3, then synced to POS). Optional/undefined treated as off.
  allowTenantProductImageUpload?: boolean;
  allowCategoryDepartmentImageUpload?: boolean;
  // Super-admin per-tenant gate for variable-price ("embedded price") barcodes.
  // When true, the tenant's POS Config page shows the barcode-pattern dropdown.
  // Optional/undefined treated as off.
  variableBarcodeEnabled?: boolean;
  barcodeAutogenEnabled?: boolean;
  productExportEnabled?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  tenant: AuthTenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setAuth: (user: AuthUser, tenant: AuthTenant | null) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  isLoading: true,
  isAuthenticated: false,
  setAuth: (user, tenant) => set({ user, tenant, isAuthenticated: true, isLoading: false }),
  clearAuth: () => set({ user: null, tenant: null, isAuthenticated: false, isLoading: false }),
  setLoading: (isLoading) => set({ isLoading }),
}));
